import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useSearchParams} from "react-router";
import {
    Typography, TextField, Grid, Button, Box, FormControl, InputLabel, FormHelperText, Autocomplete,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {Controller} from "react-hook-form";
import {ROUTES} from "../../router/config";
import {useApplicationStorage} from "../../hooks/useApplicationStorage";
import {useLeadRequest} from "../../hooks/useLeadRequest";
import {useGetCountries} from "../../hooks/useGetCountries";
import Preloader from "../../components/Preloader";
import {highlightMatch, useAddressFunctions} from "../../hooks/useAddressFunctions";
import {useAddressForm} from "./hooks/useAddressForm";

const AddressPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {applicationId} = useParams();
    const [searchParams] = useSearchParams();

    const isEdit = searchParams.has("edit");

    const {leadData, setLead, getStudents} = useApplicationStorage();

    const {putLeadRequest, isPendingLeadRequest} = useLeadRequest(applicationId);
    const {isLoading: isLoadingCountries, countriesOptions} = useGetCountries();

    const {
        handleSubmit, control, errors,
        watch, setValue, isDirty,
    } = useAddressForm(leadData);
    const country = watch("country");

    const {
        suggestions,
        isSearching,
        searchQuery,
        setSearchQuery,
        open,
        setOpen,
        autocompleteRef,
        handleAddressSearch,
        handleAddressSelect,
    } = useAddressFunctions(country, setValue);

    const onSubmit = async (data) => {
        try {
            if (isDirty) {
                const bodyData = {
                    ...data,
                    ...(leadData || {}),
                    onlineRegistrationId: applicationId,
                };

                const response = await putLeadRequest(bodyData);

                setLead({...bodyData, leadId: response.leadId});
            }

            const students = getStudents();
            const firstStudent = (students || []).find((it) => it.order === 0);

            if (firstStudent) {
                navigate(`/${applicationId}/${ROUTES.STUDENTS}/${firstStudent.childId}`);
            } else {
                navigate(isEdit ? `/${applicationId}/${ROUTES.CONFIRMATION}` : `/${applicationId}/${ROUTES.STUDENTS}`);
            }
        } catch {
            // fail silently
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className="AddressPage">
            <Typography variant="h1">
                {t("address.title")}
            </Typography>
            <Typography variant="body1" className="mt-4" gutterBottom>
                {t("address.subtitle")}
            </Typography>
            <Preloader isLoading={isLoadingCountries} overflow>
                <form className="ParentPage-form mt-12" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container rowSpacing={3} columnSpacing={2}>
                        <Grid item size={12}>
                            <FormControl fullWidth error={!!errors.country}>
                                <InputLabel required>{t("address.countryRegion")}</InputLabel>
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            {...field}
                                            value={countriesOptions.find((opt) => opt.value === field.value) || null}
                                            onChange={(event, newValue) => {
                                                field.onChange(newValue?.value || null);
                                            }}
                                            disablePortal
                                            options={countriesOptions}
                                            renderInput={(params) => <TextField {...params} placeholder={t("address.placeholders.countryRegion")} />}
                                        />
                                    )}
                                />
                                {errors.country?.message ? (
                                    <FormHelperText>{errors.country.message}</FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>
                        <Grid item size={12}>
                            <Controller
                                name="addressLine1"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <Autocomplete
                                        ref={autocompleteRef}
                                        freeSolo
                                        open={open}
                                        onOpen={() => {
                                            if (suggestions.length > 0) setOpen(true);
                                        }}
                                        onClose={(_, reason) => {
                                            if (reason === "blur" || reason === "escape") setOpen(false);
                                        }}
                                        options={suggestions}
                                        loading={isSearching}
                                        getOptionLabel={(option) =>
                                            typeof option === "string" ? option : option.text || ""
                                        }
                                        filterOptions={(x) => x}
                                        onInputChange={(_, value, reason) => {
                                            if (reason === "input") {
                                                field.onChange(value);
                                                setSearchQuery(value);
                                                handleAddressSearch(value);
                                            }
                                        }}
                                        onChange={(_, newValue) => {
                                            if (newValue && typeof newValue !== "string") {
                                                handleAddressSelect(newValue);
                                            }
                                        }}
                                        inputValue={field.value || ""}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                slotProps={{
                                                    ...params.slotProps,
                                                    htmlInput: {...params.inputProps, required: false},
                                                }}
                                                label={t("address.addressLine1")}
                                                placeholder={t("address.placeholders.addressLine1")}
                                                required
                                                error={!!error}
                                                helperText={error?.message}
                                                onBlur={field.onBlur}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <div className="w-full flex align-center">
                                                    <span>{highlightMatch(option.text, searchQuery)}</span>
                                                    {option.description && (
                                                        <span style={{color: "gray", marginLeft: 8}}>
                                                            {option.description}
                                                        </span>
                                                    )}
                                                    {option.type === "Container" && (
                                                        <ChevronRightIcon style={{marginLeft: "auto"}} />
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="addressLine2"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={t("address.addressLine2")}
                                        placeholder={t("address.placeholders.addressLine2")}
                                        error={!!errors.addressLine2}
                                        helperText={errors.addressLine2?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="code"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {required: false}}}
                                        label={t("address.zipCode")}
                                        placeholder={t("address.placeholders.zipCode")}
                                        required
                                        error={!!errors.code}
                                        helperText={errors.code?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="city"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {required: false}}}
                                        label={t("address.city")}
                                        placeholder={t("address.placeholders.city")}
                                        required
                                        error={!!errors.city}
                                        helperText={errors.city?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="area"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {required: false}}}
                                        label={t("address.state")}
                                        placeholder={t("address.placeholders.state")}
                                        required
                                        error={!!errors.area}
                                        helperText={errors.area?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={12} className="mt-4">
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2}}>
                                <Button
                                    variant="back"
                                    onClick={handleBack}
                                    startIcon={<PlayArrowIcon className="rotate-180" fontSize="large" />}
                                >
                                    {t("button.back")}
                                </Button>
                                <Button
                                    variant="accent"
                                    type="submit"
                                    loading={isPendingLeadRequest}
                                >
                                    {t("button.nextStep")}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Preloader>
        </div>
    );
};

export default AddressPage;
